import { ReactNode, useState, useRef, useEffect, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { PolicyCard, PolicySearchBar } from '@practium/features/policy';
import styles from './PoliciesLayout.module.css';
import { LoadingState, EmptyState } from '@practium/ui';
import { Policy } from '@practium/features/policy';

interface PoliciesLayoutProps {
	children: ReactNode;
	policies: Policy[];
	selectedPolicy: Policy | null;
	isLoading: boolean;
	updatingPolicyId: string | null;
	onSearch: (query: string) => void;
	onTogglePolicy: (policyId: string, isActive: boolean) => void;
	onSelectPolicy: (policy: Policy) => void;
	onAddPolicy?: () => void;
	headerContent: ReactNode;
}

export const PoliciesLayout = ({
	children,
	policies,
	selectedPolicy,
	isLoading,
	updatingPolicyId,
	onSearch,
	onTogglePolicy,
	onSelectPolicy,
	onAddPolicy,
	headerContent,
}: PoliciesLayoutProps) => {
	const getInitialHeight = () => {
		if (globalThis.window === undefined) return 30;
		if (globalThis.window.innerWidth <= 768) return 12;
		if (globalThis.window.innerWidth <= 1224) return 10;
		return 30;
	};

	const [viewerHeight, setViewerHeight] = useState(getInitialHeight);
	const [isDragging, setIsDragging] = useState(false);
	const [shouldHighlight, setShouldHighlight] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const viewerRef = useRef<HTMLDivElement>(null);
	const dragStartY = useRef(0);
	const dragStartHeight = useRef(0);
	const previousPolicyId = useRef<string | null>(null);
	const userHasDragged = useRef(false);
	const savedScrollPosition = useRef(0);

	const scrollViewerToTop = useCallback(() => {
		if (viewerRef.current) {
			const viewerContent = viewerRef.current.querySelector('[class*="viewerContent"]');
			if (viewerContent) {
				viewerContent.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
	}, []);

	useEffect(() => {
		const isMediumOrSmall = globalThis.window !== undefined && globalThis.window.innerWidth <= 1224;

		if (selectedPolicy && selectedPolicy._id !== previousPolicyId.current) {
			const isFirstSelection = previousPolicyId.current === null;
			previousPolicyId.current = selectedPolicy._id;

			if (!isMediumOrSmall || isFirstSelection) {
				if (isMediumOrSmall) {
					setShouldHighlight(true);
					setTimeout(() => setShouldHighlight(false), 800);

					setTimeout(() => {
						scrollViewerToTop();
					}, 100);
				} else {
					scrollViewerToTop();
				}
			}
		}
	}, [selectedPolicy, scrollViewerToTop]);

	const handleDragStart = (clientY: number) => {
		if (globalThis.window.innerWidth > 1224) return;
		setIsDragging(true);
		dragStartY.current = clientY;
		dragStartHeight.current = viewerHeight;
	};

	const handleDragMove = useCallback(
		(clientY: number) => {
			if (!isDragging || !containerRef.current) return;

			const containerHeight = containerRef.current.clientHeight;
			const deltaY = dragStartY.current - clientY;
			const deltaPercent = (deltaY / containerHeight) * 100;
			const newHeight = Math.min(Math.max(dragStartHeight.current + deltaPercent, 12), 100);

			setViewerHeight(newHeight);
		},
		[isDragging]
	);

	const handleDragEnd = useCallback(() => {
		setIsDragging(false);
		userHasDragged.current = true;

		const initialHeight = getInitialHeight();
		const threshold = 5;

		if (Math.abs(viewerHeight - initialHeight) < threshold) {
			setViewerHeight(initialHeight);
		}
	}, [viewerHeight]);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
		const handleTouchMove = (e: TouchEvent) => handleDragMove(e.touches[0].clientY);
		const handleEnd = () => handleDragEnd();

		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('touchmove', handleTouchMove);
			document.addEventListener('mouseup', handleEnd);
			document.addEventListener('touchend', handleEnd);
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('mouseup', handleEnd);
			document.removeEventListener('touchend', handleEnd);
		};
	}, [isDragging, handleDragMove, handleDragEnd]);

	useEffect(() => {
		const handleResize = () => {
			if (!userHasDragged.current) {
				setViewerHeight(getInitialHeight());
			}
		};

		globalThis.window.addEventListener('resize', handleResize);
		return () => globalThis.window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (viewerRef.current) {
			const viewerContent = viewerRef.current.querySelector('[class*="viewerContent"]');
			if (viewerContent) {
				const saveScroll = () => {
					savedScrollPosition.current = viewerContent.scrollTop;
				};

				const restoreScroll = () => {
					if (savedScrollPosition.current > 0) {
						viewerContent.scrollTop = savedScrollPosition.current;
					}
				};

				saveScroll();

				const timeoutId = setTimeout(restoreScroll, 0);

				return () => clearTimeout(timeoutId);
			}
		}
	}, [children]);

	if (isLoading) {
		return <LoadingState />;
	}

	const isMediumOrSmall = globalThis.window !== undefined && globalThis.window.innerWidth <= 1224;

	return (
		<div
			className={styles.container}
			ref={containerRef}
			style={
				isMediumOrSmall
					? ({
							'--viewer-height': `${viewerHeight}%`,
						} as React.CSSProperties)
					: undefined
			}
		>
			<aside className={styles.sidebar}>
				<header className={styles.sidebarHeader}>
					<h2 className={styles.sidebarTitle}>Políticas do Practium</h2>
					{onAddPolicy && (
						<button
							className={styles.addButton}
							aria-label="Adicionar política"
							onClick={onAddPolicy}
						>
							<AddIcon />
						</button>
					)}
				</header>

				<div className={styles.searchSection}>
					<PolicySearchBar onSearch={onSearch} placeholder="Buscar política..." />
				</div>

				{policies.length === 0 ? (
					<div className={styles.empty}>
						<EmptyState
							icon={<DescriptionOutlinedIcon />}
							title="Nenhuma política encontrada"
							description="Clique no botão + para adicionar uma nova política."
						/>
					</div>
				) : (
					<div className={styles.policiesGrid}>
						{policies.map((policy) => (
							<PolicyCard
								key={policy._id}
								policy={policy}
								isSelected={selectedPolicy?._id === policy._id}
								isUpdating={updatingPolicyId === policy._id}
								onToggle={onTogglePolicy}
								onClick={() => onSelectPolicy(policy)}
							/>
						))}
					</div>
				)}
			</aside>

			{isMediumOrSmall && (
				<button
					className={`${styles.resizeHandle} ${shouldHighlight ? styles.resizeHandleHighlight : ''}`}
					onMouseDown={(e) => handleDragStart(e.clientY)}
					onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
					aria-label="Redimensionar painel"
				>
					<div className={styles.resizeHandleBar} />
					{shouldHighlight && <div className={styles.resizeHandleGlow} />}
				</button>
			)}

			<main
				className={`${styles.viewer} ${shouldHighlight ? styles.viewerHighlight : ''}`}
				ref={viewerRef}
			>
				{headerContent}

				<div className={styles.viewerContent}>{children}</div>
			</main>
		</div>
	);
};
